import React, { useState, useEffect, useMemo } from "react";
import { X, Plus } from "lucide-react";
import Button from "./Buttons";
import TPRMNewVendorModal from "./TPRMNewVendorModal";

const TPRMVendorSelectModal = ({
  isOpen,
  onClose,
  onVendorSelected,
  vendors = [],
  existingVendorIds = [],
  onVendorCreated,
  dealId = null
}) => {
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [localVendors, setLocalVendors] = useState(vendors);

  // Update local vendors when prop changes
  useEffect(() => {
    setLocalVendors(vendors);
  }, [vendors]);

  // Filter out vendors that already have assessments
  const availableVendors = useMemo(() => {
    const existingSet = new Set(existingVendorIds.map(id => Number(id)));
    return localVendors.filter(v => !existingSet.has(Number(v.vendorId)));
  }, [localVendors, existingVendorIds]);

  // Auto-show create modal if no available vendors
  useEffect(() => {
    if (isOpen && availableVendors.length === 0) {
      setShowCreateModal(true);
    }
  }, [isOpen, availableVendors.length]);

  const handleVendorCreated = (newVendor) => {
    // Add the new vendor to local list and select it
    setLocalVendors((prev) => [...prev, newVendor]);
    setSelectedVendorId(String(newVendor.vendorId));
    setShowCreateModal(false);
    // Notify parent to refresh their vendor list
    onVendorCreated?.(newVendor);
  };

  const handleSave = () => {
    if (!selectedVendorId) {
      alert("Please select a vendor.");
      return;
    }

    const selectedVendor = localVendors.find(
      (v) => String(v.vendorId) === selectedVendorId
    );

    if (selectedVendor) {
      onVendorSelected(selectedVendor);
    }
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
    // If there are no available vendors and user closes create modal, close this modal too
    if (availableVendors.length === 0) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Sort available vendors alphabetically
  const sortedVendors = [...availableVendors].sort((a, b) =>
    (a.vendorName || "").localeCompare(b.vendorName || "")
  );

  return (
    <>
      <div className="fixed inset-0 bg-gray-700 bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-[480px] shadow-lg">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-content-primary">
              Select Vendor
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Vendor Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vendor
            </label>
            <div className="flex gap-2">
              <select
                value={selectedVendorId}
                onChange={(e) => setSelectedVendorId(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select Vendor --</option>
                {sortedVendors.map((vendor) => (
                  <option key={vendor.vendorId} value={vendor.vendorId}>
                    {vendor.vendorName}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center"
                title="Add new vendor"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!selectedVendorId}
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Nested Create Vendor Modal */}
      {showCreateModal && (
        <TPRMNewVendorModal
          isOpen={showCreateModal}
          onClose={handleCreateModalClose}
          onVendorCreated={handleVendorCreated}
          dealId={dealId}
        />
      )}
    </>
  );
};

export default TPRMVendorSelectModal;
